import React, { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { 
  User,
  Settings,
  LogOut,
  Shield,
  Search,
} from 'lucide-react'
import { useAuth } from '../../shared/contexts/AuthContext'
import { ThemeToggle } from '../theme/ThemeToggle'
import NotificationBell from '../notifications/NotificationBell'
import PushNotificationSettings from '../notifications/PushNotificationSettings'
import { canAdmin, isSuperAdmin } from '../../lib/utils/roleUtils'
import { useSessionActivity } from '../../hooks/useSessionActivity'
import saltLogo from '../../styles/SALT Program Simple.png'
import { debug } from '../../lib/utils/debug'
import GroupContextSwitcher from '../common/GroupContextSwitcher'
import TenantContextIndicator from '../common/TenantContextIndicator'
import GlobalSearch from '../search/GlobalSearch'
import KeyboardShortcutsHelp from '../common/KeyboardShortcutsHelp'
import { useKeyboardShortcuts } from '../../shared/hooks/useKeyboardShortcuts'
import SkipLink from '../common/SkipLink'
import FeatureGate from '../common/FeatureGate'
import { useFeatureFlag } from '../../shared/hooks/useFeatureFlag'
import {
  DESKTOP_ADMIN_NAV,
  DESKTOP_OVERFLOW_NAV,
  DESKTOP_PRIMARY_NAV,
  resolveMobileBottomNav,
  type NavItem,
} from './navigationConfig'

export default function Layout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { isEnabled: journalingEnabled } = useFeatureFlag('journaling', { defaultValue: true })
  const mobileBottomNav = resolveMobileBottomNav(journalingEnabled)
  const [mobileProfileMenuOpen, setMobileProfileMenuOpen] = useState(false)
  const [desktopProfileMenuOpen, setDesktopProfileMenuOpen] = useState(false)
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showGlobalSearch, setShowGlobalSearch] = useState(false)
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false)

  // Register global keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'k',
      modifiers: ['ctrl'],
      handler: () => {
        setShowGlobalSearch(true)
        // Focus search input
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder="Search..."]') as HTMLInputElement
          if (searchInput) {
            searchInput.focus()
          }
        }, 100)
      },
      description: 'Open search'
    },
    {
      key: '?',
      modifiers: ['shift'],
      handler: () => {
        setShowKeyboardShortcuts(true)
      },
      description: 'Show keyboard shortcuts'
    },
    {
      key: 'Escape',
      handler: () => {
        if (showGlobalSearch) {
          setShowGlobalSearch(false)
          setSearchQuery('')
        }
        if (showKeyboardShortcuts) {
          setShowKeyboardShortcuts(false)
        }
      },
      description: 'Close modals'
    }
  ])
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const profileButtonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  // Track session activity
  useSessionActivity()

  const mobileDockLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 rounded-2xl transition-all duration-200 ${
      isActive
        ? 'text-salt-navy bg-gradient-to-b from-salt-gold to-salt-gold-soft shadow-lg shadow-salt-gold/30'
        : 'text-white/50 hover:text-white/90'
    }`

  const toneIconClass = (tone?: NavItem['tone']) => {
    switch (tone) {
      case 'warning':
        return 'text-orange-600 dark:text-orange-400'
      case 'danger':
        return 'text-red-600 dark:text-red-400'
      case 'default':
      case undefined:
        return ''
      default: {
        const _exhaustive: never = tone
        return _exhaustive
      }
    }
  }

  const renderDesktopNavItem = (item: NavItem) => {
    const Icon = item.icon
    const link = (
      <NavLink
        to={item.to}
        end={item.end}
        aria-label={item.label}
        title={item.label}
        className={({ isActive }) => `salt-dock-item${isActive ? ' is-active' : ''}`}
      >
        <Icon className={`w-5 h-5 ${toneIconClass(item.tone)}`} />
      </NavLink>
    )
    if (item.featureGate) {
      return (
        <FeatureGate key={item.id} feature={item.featureGate} hideWhenDisabled>
          {link}
        </FeatureGate>
      )
    }
    return <React.Fragment key={item.id}>{link}</React.Fragment>
  }

  const renderMobileNavItem = (item: NavItem) => {
    const Icon = item.icon
    return (
      <NavLink key={item.id} to={item.to} end={item.end} className={mobileDockLinkClass}>
        <Icon className="w-5 h-5" />
        <span className="text-[10px] font-bold uppercase tracking-wide">{item.label}</span>
      </NavLink>
    )
  }

  const visibleAdminNav = DESKTOP_ADMIN_NAV.filter((item) => {
    if (item.requireSuperAdmin) {
      return Boolean(user?.role && isSuperAdmin(user.role))
    }
    if (item.requireAdmin) {
      return Boolean(user?.role && canAdmin(user.role))
    }
    return true
  })

  // Listen for notification settings event
  useEffect(() => {
    const handleOpenNotificationSettings = () => {
      setNotificationSettingsOpen(true)
    }

    window.addEventListener('open-notification-settings', handleOpenNotificationSettings)
    
    return () => {
      window.removeEventListener('open-notification-settings', handleOpenNotificationSettings)
    }
  }, [])

  const handleSignOut = async () => {
    try {
      debug.log('🔐 Layout: Starting sign out...')
      await signOut()
      debug.log('✅ Layout: Sign out successful, navigating to auth')
      navigate('/auth')
    } catch (error) {
      debug.error('❌ Layout: Error signing out:', error)
      
      // Fallback: clear local state and redirect even if server logout failed
      debug.log('🔄 Layout: Using fallback logout - clearing local state')
      localStorage.removeItem('sb-iasrrcndbdelwetoljkp-auth-token')
      localStorage.removeItem('supabase.auth.refreshToken')
      localStorage.removeItem('salt-auth-token')
      
      // Force a page reload to clear all state
      window.location.href = '/auth'
    }
  }

  const updateDropdownPosition = () => {
    if (profileButtonRef.current) {
      const rect = profileButtonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 224 // 224px is the width of the dropdown (w-56 = 14rem = 224px)
      })
    }
  }

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setMobileProfileMenuOpen(false)
        setDesktopProfileMenuOpen(false)
      }
    }

    if (mobileProfileMenuOpen || desktopProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      updateDropdownPosition()
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [mobileProfileMenuOpen, desktopProfileMenuOpen])

  return (
    <div className="salt-shell salt-grain relative flex min-h-screen flex-col overflow-x-hidden">
      <SkipLink targetId="main-content" />
      <div className="salt-mesh pointer-events-none fixed inset-0 z-0" aria-hidden />

      {/* Desktop floating dock */}
      <aside
        className="fixed left-4 top-1/2 z-50 hidden -translate-y-1/2 md:flex md:flex-col md:items-center md:gap-3"
        aria-label="Primary navigation"
      >
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="salt-dock mb-1 flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl p-1.5 transition-transform hover:scale-105"
          aria-label="Go to Home"
        >
          <img
            src={saltLogo}
            alt=""
            className="h-full w-full rounded-xl object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        </button>
        <nav className="salt-dock flex flex-col gap-1.5 rounded-[1.75rem] p-2">
          {DESKTOP_PRIMARY_NAV.map(renderDesktopNavItem)}
          {visibleAdminNav.length > 0 && (
            <>
              <div className="mx-2 my-1 h-px bg-white/10" />
              {visibleAdminNav.map(renderDesktopNavItem)}
            </>
          )}
        </nav>
      </aside>

      {/* Top command bar — glass pill, full width */}
      <header className="fixed left-0 right-0 top-0 z-40 px-4 pt-4 md:pl-24">
        <div className="salt-glass-dark mx-auto flex max-w-[1600px] items-center gap-3 rounded-2xl px-4 py-2.5">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
            <input
              type="text"
              placeholder="Search anything…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                if (e.target.value.trim()) setShowGlobalSearch(true)
              }}
              onFocus={() => {
                if (searchQuery.trim()) setShowGlobalSearch(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowGlobalSearch(false)
                if (e.key === 'Enter' && searchQuery.trim()) setShowGlobalSearch(true)
              }}
              className="w-full rounded-xl border border-white/8 bg-white/5 py-2 pl-10 pr-4 text-sm text-white placeholder:text-white/35 focus:border-salt-gold/40 focus:outline-none focus:ring-1 focus:ring-salt-gold/30"
            />
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <GroupContextSwitcher />
            <TenantContextIndicator />
          </div>
          <NotificationBell />
          <ThemeToggle />
          <div className="relative">
            <button
              ref={profileButtonRef}
              onClick={() => {
                setDesktopProfileMenuOpen(!desktopProfileMenuOpen)
                setMobileProfileMenuOpen(!mobileProfileMenuOpen)
              }}
              className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-salt-gold to-salt-gold-soft ring-2 ring-white/10 transition hover:ring-salt-gold/50"
              aria-label="Profile menu"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <User className="h-4 w-4 text-salt-navy" />
              )}
            </button>
            {(mobileProfileMenuOpen || desktopProfileMenuOpen) &&
              createPortal(
                <div
                  className="salt-glass-dark fixed z-[999999] w-60 rounded-2xl p-2 shadow-2xl"
                  ref={profileMenuRef}
                  style={{ top: dropdownPosition.top, left: dropdownPosition.left, zIndex: 999999 }}
                >
                  <div className="p-1">
                    {DESKTOP_OVERFLOW_NAV.map((item) => {
                      const Icon = item.icon
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setMobileProfileMenuOpen(false)
                            setDesktopProfileMenuOpen(false)
                            navigate(item.to)
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-white/80 transition hover:bg-white/8"
                        >
                          <Icon className="h-4 w-4 flex-shrink-0 text-salt-gold" />
                          <span className="truncate">{item.label}</span>
                        </button>
                      )
                    })}
                    <hr className="my-2 border-white/10" />
                    {user?.role && canAdmin(user.role) && (
                      <button
                        type="button"
                        onClick={() => {
                          setMobileProfileMenuOpen(false)
                          setDesktopProfileMenuOpen(false)
                          navigate('/admin')
                        }}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-white/80 transition hover:bg-white/8"
                      >
                        <Shield className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">Admin Console</span>
                      </button>
                    )}
                    {user?.role && isSuperAdmin(user.role) && (
                      <button
                        type="button"
                        onClick={() => {
                          setMobileProfileMenuOpen(false)
                          setDesktopProfileMenuOpen(false)
                          navigate('/super-admin')
                        }}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-white/80 transition hover:bg-white/8"
                      >
                        <Shield className="h-4 w-4 flex-shrink-0 text-red-400" />
                        <span className="truncate">Platform Admin</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setMobileProfileMenuOpen(false)
                        setDesktopProfileMenuOpen(false)
                        window.dispatchEvent(new CustomEvent('open-notification-settings'))
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-white/80 transition hover:bg-white/8"
                    >
                      <Settings className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">Notifications</span>
                    </button>
                    <hr className="my-2 border-white/10" />
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-red-400 transition hover:bg-red-500/10"
                    >
                      <LogOut className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">Sign Out</span>
                    </button>
                  </div>
                </div>,
                document.body
              )}
          </div>
        </div>
      </header>

      {/* Full-bleed main stage */}
      <main
        id="main-content"
        className="relative z-10 flex min-h-0 flex-1 w-full flex-col pt-[5.5rem] md:pl-20"
        tabIndex={-1}
      >
        <div className="flex min-h-0 w-full flex-1 flex-col pb-28 md:pb-0">
          <Outlet />
        </div>
      </main>

      {/* Mobile floating dock */}
      <nav
        className="salt-dock fixed bottom-4 left-4 right-4 z-50 rounded-[1.75rem] px-2 py-1.5 md:hidden"
        aria-label="Mobile primary"
      >
        <div className="flex items-stretch justify-around">
          {mobileBottomNav.map(renderMobileNavItem)}
        </div>
      </nav>

      <GlobalSearch
        isOpen={showGlobalSearch}
        onClose={() => {
          setShowGlobalSearch(false)
          setSearchQuery('')
        }}
        searchQuery={searchQuery}
      />

      <KeyboardShortcutsHelp
        isOpen={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {notificationSettingsOpen &&
        createPortal(
          <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
            <div className="salt-glass-dark max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl p-6">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-white">Notification Settings</h2>
                <button
                  onClick={() => setNotificationSettingsOpen(false)}
                  className="text-white/40 transition hover:text-white"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <PushNotificationSettings />
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
